import { normalizeParentSelection } from '../tree';
import type { DropdownValue, FlatItem } from '../../types';

export { isFullySelected } from './isFullySelected';
export { getTopmostSelectedLabels } from './getTopmostSelectedLabels';

export function toSelectableValues(
  nextValueInput: DropdownValue[] | DropdownValue | null,
  selectableByValue: Map<DropdownValue, boolean>,
): DropdownValue[] {
  if (Array.isArray(nextValueInput)) {
    return nextValueInput.filter((value) => selectableByValue.get(value));
  }

  if (!nextValueInput || !selectableByValue.get(nextValueInput)) {
    return [];
  }

  return [nextValueInput];
}

export function getNextSingleSelection(selectableValues: DropdownValue[]): DropdownValue[] {
  return selectableValues.slice(-1);
}

export function getNextSelectedValues({
  previousValues,
  nextValueInput,
  multiple,
  selectableByValue,
  descendantsByValue,
  itemsByDepthDesc,
}: {
  previousValues: DropdownValue[];
  nextValueInput: DropdownValue[] | DropdownValue | null;
  multiple: boolean;
  selectableByValue: Map<DropdownValue, boolean>;
  descendantsByValue: Map<DropdownValue, DropdownValue[]>;
  itemsByDepthDesc: FlatItem[];
}): DropdownValue[] {
  const selectableValues = toSelectableValues(nextValueInput, selectableByValue);

  if (!multiple) {
    return getNextSingleSelection(selectableValues);
  }

  const nextSet = new Set(selectableValues);

  syncDescendantSelections(previousValues, selectableValues, nextSet, descendantsByValue);

  const normalized = normalizeParentSelection(
    nextSet,
    itemsByDepthDesc,
    descendantsByValue,
    selectableByValue,
  );

  return [...normalized];
}

export function syncDescendantSelections(
  previousValues: DropdownValue[],
  selectableValues: DropdownValue[],
  nextSet: Set<DropdownValue>,
  descendantsByValue: Map<DropdownValue, DropdownValue[]>,
): void {
  const previousSet = new Set(previousValues);

  const addedValues = selectableValues.filter((value) => !previousSet.has(value));
  const removedValues = previousValues.filter((value) => !nextSet.has(value));

  for (const toggledOn of addedValues) {
    const descendants = descendantsByValue.get(toggledOn) ?? [];

    for (const descendant of descendants) {
      nextSet.add(descendant);
    }
  }

  for (const toggledOff of removedValues) {
    const descendants = descendantsByValue.get(toggledOff) ?? [];

    for (const descendant of descendants) {
      nextSet.delete(descendant);
    }
  }
}

export function buildPartialSelectionMap(
  allItems: FlatItem[],
  descendantsByValue: Map<DropdownValue, DropdownValue[]>,
  selectableByValue: Map<DropdownValue, boolean>,
  selectedSet: Set<DropdownValue>,
): Map<DropdownValue, boolean> {
  const partialByValue = new Map<DropdownValue, boolean>();

  for (const item of allItems) {
    const descendants = descendantsByValue.get(item.value) ?? [];
    const selectedDescendantsCount = descendants.filter((value) => selectedSet.has(value)).length;
    const hasSelectedDescendants = descendants.length > 0 && selectedDescendantsCount > 0;
    const isSelected = selectedSet.has(item.value);
    const isPartial =
      hasSelectedDescendants &&
      (selectedDescendantsCount < descendants.length || (item.type === 'value' && !isSelected));

    partialByValue.set(item.value, isPartial);
  }

  return partialByValue;
}
